// hooks/useLoginForm.js
import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";
const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutos

// Canoniza el rol a un grupo lógico para la app
const toLower = (s) => (s || "").toString().toLowerCase();
const rolAGrupo = (rol) => {
  const r = toLower(rol);
  if (r.includes("docente")) return "Docente"; // cubre Docente, de planta, de cátedra
  if (r === "admin" || r === "administrador") return "Administrador";
  if (r === "administrativo") return "Administrativo";
  if (r === "estudiante" || r === "student") return "Estudiante";
  return rol || "Estudiante";
};

const useLoginForm = () => {
  const [credentials, setCredentials] = useState({
    correo: "",
    contrasena: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let inactivityTimer;

    const handleInactivityTimeout = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("usuario");

      Swal.fire({
        title:
          '<h1 class="text-2xl font-bold text-black mb-2">Sesión expirada</h1>',
        html: `
          <div class="text-gray-700 text-base">
            <p class="mb-2">Por favor, inicie sesión nuevamente para continuar.</p>
          </div>
        `,
        icon: "warning",
        iconColor: "#fa2a00",
        confirmButtonText:
          '<span class="flex items-center"> Iniciar sesión</span>',
        focusConfirm: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: "rounded-xl shadow-xl border-none max-w-md p-6",
          confirmButton: `
            bg-green-600 hover:bg-green-700
            text-white font-semibold
            py-2 px-4 rounded-lg
            shadow hover:shadow-md
            transition-all duration-300
            transform hover:-translate-y-0.5
            mr-2
          `,
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) navigate("/");
      });
    };

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(handleInactivityTimeout, INACTIVITY_TIMEOUT);
    };

    const events = ["mousemove", "keypress", "scroll", "click", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, resetInactivityTimer));
    resetInactivityTimer();

    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((ev) =>
        window.removeEventListener(ev, resetInactivityTimer)
      );
    };
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // correo siempre en minúsculas
    if (name === "correo") {
      setCredentials((prev) => ({
        ...prev,
        correo: (value || "").toLowerCase(),
      }));
    } else {
      setCredentials((prev) => ({ ...prev, [name]: value }));
    }
  };

  const togglePasswordVisibility = () => setShowPassword((s) => !s);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const payload = {
        ...credentials,
        correo: (credentials.correo || "").toLowerCase(), // por si acaso
      };

      const { data } = await axios.post(
        `${API_BASE_URL}/api/auth/login`,
        payload
      );

      if (data?.token && data?.usuario) {
        // Enriquecer usuario con rolGrupo
        const grupo = rolAGrupo(data.usuario.rol);
        const usuarioEnriquecido = { ...data.usuario, rolGrupo: grupo };

        // Persistencia básica
        localStorage.setItem("token", data.token);
        localStorage.setItem("usuario", JSON.stringify(usuarioEnriquecido));

        // ADICIÓN: iniciar sesión de tiempo (login → …)
        try {
          // Limpia cualquier sessionId previa por seguridad
          localStorage.removeItem("sessionId");

          const { data: startResp } = await axios.post(
            `${API_BASE_URL}/api/tiempos/session/start`,
            { loginAt: new Date().toISOString() },
            { headers: { Authorization: `Bearer ${data.token}` } }
          );

          if (startResp?.sessionId) {
            localStorage.setItem("sessionId", String(startResp.sessionId));
          }
        } catch (e) {
          console.error(
            "No se pudo registrar el inicio de sesión de tiempo:",
            e
          );
          // No bloquea el login si falla el tracking de tiempo
        }

        // Navegación por grupo
        if (grupo === "Administrador") {
          navigate("/admin-panel");
        } else {
          navigate("/user-dashboard");
        }
      } else {
        setError("Respuesta del servidor inválida");
      }
    } catch (error) {
      console.error("Error en el inicio de sesión:", error);
      if (error.response?.data?.mensaje) {
        setError(error.response.data.mensaje);
      } else if (error.request) {
        setError(
          "No se pudo conectar con el servidor. Por favor, intenta de nuevo más tarde."
        );
      } else {
        setError("Error al iniciar sesión. Por favor, intenta de nuevo.");
      }
    }
  };

  return {
    credentials,
    showPassword,
    error,
    handleChange,
    togglePasswordVisibility,
    handleSubmit,
  };
};

export default useLoginForm;
